import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { Selection } from 'src/domains/sportsbook/webview/components/Selection';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { MARKET_NAMES } from 'src/domains/sportsbook/webview/components/goalscorerMarketGroup/GoalscorerMarketGroup';
import { ExpandButton } from 'src/domains/sportsbook/webview/components/expandButton/ExpandButton';
import { GoalScorerType, GoalScorerMarketState, ToggleExpandList } from './GoalScorerMarket.state';
import { GoalscorerName } from 'src/domains/sportsbook/webview/components/goalscorerMarketGroup/GoalscorerMarketGroup.style';
import {
    GoalScorerMarketWrapper,
    GoalScorerMarketListWrapper,
    GoalScorerMarketListItem,
    MarketGroupCaptionDesktop,
    CaptionGroupDesktop,
    CaptionDesktop,
} from 'src/domains/sportsbook/webview/components/goalscorerMarketGroup/GoalScorerMarket.style';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

interface GoalScorerMarketListItemWrapperPropsType {
    goalScorer: GoalScorerType;
    variant: DefaultComponentVariantType | null;
}

export const GoalScorerMarketListItemWrapper = observer(
    'GoalScorerMarketListItemWrapper',
    (props: GoalScorerMarketListItemWrapperPropsType): JSX.Element | null => {
        const {
            goalScorer: { name, first, anytime, hatTrick },
            variant,
        } = props;

        return (
            <GoalScorerMarketListItem
                key={name}
                variant={variant}
            >
                <GoalscorerName
                    templateId=''
                    variant={variant}
                >
                    {name === 'No goalscorer' ? (
                        <I18n
                            langKey='selection.football.match.no-goalscorer'
                            defaultText='No goalscorer'
                        />
                    ) : (
                        name
                    )}
                </GoalscorerName>
                <Selection
                    borderBottom={true}
                    borderLeft={true}
                    borderRight={true}
                    selectionId={first ?? undefined}
                />
                <Selection
                    borderBottom={true}
                    borderLeft={true}
                    borderRight={true}
                    selectionId={anytime ?? undefined}
                />
                <Selection
                    borderBottom={true}
                    borderLeft={true}
                    selectionId={hatTrick ?? undefined}
                />
            </GoalScorerMarketListItem>
        );
    }
);

interface GoalScorerMarketListPropsType {
    goalScorerList: Array<GoalScorerType>;
    isExpanded: boolean;
    variant: DefaultComponentVariantType | null;
}

const GoalScorerMarketList = observer(
    'GoalScorerMarketList',
    (props: GoalScorerMarketListPropsType): JSX.Element | null => {
        const { goalScorerList, isExpanded, variant } = props;

        return (
            <GoalScorerMarketListWrapper variant={variant}>
                {goalScorerList.length > 0 ? (
                    <MarketGroupCaptionDesktop variant={variant}>
                        <CaptionDesktop
                            isFirst={true}
                            variant={variant}
                        >
                            <I18n
                                langKey='events.goalscorer-market.player'
                                defaultText='Player'
                            />
                        </CaptionDesktop>

                        <CaptionGroupDesktop variant={variant}>
                            {Object.keys(MARKET_NAMES).map((templateId: string) => {
                                const marketName = MARKET_NAMES[templateId];
                                return (
                                    <CaptionDesktop
                                        key={templateId}
                                        variant={variant}
                                    >
                                        {marketName}
                                    </CaptionDesktop>
                                );
                            })}
                        </CaptionGroupDesktop>
                    </MarketGroupCaptionDesktop>
                ) : null}

                {goalScorerList.slice(0, isExpanded === true ? goalScorerList.length : 6).map((elem) => {
                    return (
                        <GoalScorerMarketListItemWrapper
                            key={elem.name}
                            goalScorer={elem}
                            variant={variant}
                        />
                    );
                })}
            </GoalScorerMarketListWrapper>
        );
    }
);

export interface GoalScorerMarketPropsType {
    state: GoalScorerMarketState;
}

export const GoalScorerMarket = observer('GoalScorerMarket', ({ state }: GoalScorerMarketPropsType) => {
    const [toggleState] = useState(() => new ToggleExpandList());
    const common = useCommon();
    const configState = ConfigComponents.get(common).config;
    const { sportsbookVariant } = configState.componentVariants;

    return (
        <>
            <GoalScorerMarketWrapper variant={sportsbookVariant}>
                <GoalScorerMarketList
                    goalScorerList={state.goalScorerListHome}
                    isExpanded={toggleState.isExpanded}
                    variant={sportsbookVariant}
                />
                <GoalScorerMarketList
                    goalScorerList={state.goalScorerListAway}
                    isExpanded={toggleState.isExpanded}
                    variant={sportsbookVariant}
                />
            </GoalScorerMarketWrapper>

            {state.noGoalScore !== null && (toggleState.isExpanded || state.isPossibleToExpandList === false) ? (
                <GoalScorerMarketListItemWrapper
                    goalScorer={state.noGoalScore}
                    variant={sportsbookVariant}
                />
            ) : null}

            {state.isPossibleToExpandList ? (
                <ExpandButton
                    isExpanded={toggleState.isExpanded}
                    toggleExpand={toggleState.toggleExpand}
                />
            ) : null}
        </>
    );
});
